import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  PopoverBody,
  PopoverFooter,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  AsyncCreatableSelect,
  type GroupBase,
  type MultiValue,
  type OptionBase,
  Select,
  type SelectInstance,
  type SingleValue,
} from 'chakra-react-select';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { OptionProps } from 'react-select';
import { useMount } from 'react-use';

import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import { FilterId, type FreeTextFilter } from '../../types/filters';
import { FilterSelectFooter } from '../FilterSelectFooter';
import { ToggleSelect } from '../ToggleSelect';
import styles from './filterSelectTheme.module.css';

interface FreeTextFilterProps {
  filter: FreeTextFilter;
  closeMenu: () => void;
  onSave: (filter: FreeTextFilter) => void;
  onBack?: () => void;
  isNewInstance?: boolean;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
}

interface KeywordOption {
  label: string;
  value: string;
}

interface AnyOfNoneOfOption extends OptionBase {
  label: string;
  displayLabel: string;
  value: boolean;
  showDefault?: boolean;
}

interface InlineAnyOfNoneOfDropdownProps {
  isNot: boolean;
  onChange: (isNot: boolean) => void;
}

const anyOfNoneOfOptions: AnyOfNoneOfOption[] = [
  {
    label: 'any of',
    displayLabel: 'any of',
    value: false,
    showDefault: true,
  },
  {
    label: 'none of',
    displayLabel: 'none of',
    value: true,
  },
];

const CustomAnyOfNoneOfOption = (
  props: OptionProps<AnyOfNoneOfOption, false>
) => {
  const { data, innerRef, innerProps, isFocused } = props;
  return (
    <Box
      ref={innerRef}
      className={classNames(
        styles.optionRow,
        isFocused && styles.optionRowFocused
      )}
      {...innerProps}
    >
      <Text as="span" className={styles.optionLabel}>
        {data.label}
      </Text>
      {data.showDefault && (
        <Text as="span" className={styles.optionDefault}>
          Default
        </Text>
      )}
    </Box>
  );
};

const InlineAnyOfNoneOfDropdown = ({
  isNot,
  onChange,
}: InlineAnyOfNoneOfDropdownProps) => {
  const handleChange = (selected: SingleValue<AnyOfNoneOfOption>) => {
    if (selected) {
      onChange(selected.value);
    }
  };

  const formatOptionLabel = ({ displayLabel }: AnyOfNoneOfOption) => {
    return displayLabel;
  };

  return (
    <Box
      display="inline-block"
      position="relative"
      zIndex={10}
      overflow="visible"
      minWidth="60px"
    >
      <Select<AnyOfNoneOfOption>
        options={anyOfNoneOfOptions}
        value={anyOfNoneOfOptions.find((opt) => opt.value === isNot) || null}
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        size="sm"
        isSearchable={false}
        menuPortalTarget={document.body}
        components={{
          // Type assertion needed due to react-select version mismatch between chakra-react-select and react-select
          // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
          Option: CustomAnyOfNoneOfOption as any,
        }}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          option: (base) => ({
            ...base,
            backgroundColor: 'transparent',
          }),
          control: (base) => ({
            ...base,
            minHeight: 20,
          }),
        }}
        chakraStyles={{
          container: (provided) => ({
            ...provided,
            width: '100%',
          }),
          control: (provided) => ({
            ...provided,
            minHeight: '20px',
            height: '20px',
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            padding: 0,
            gap: '2px',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            '&:focus-within': {
              border: 'none',
              boxShadow: 'none',
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: 0,
            fontSize: '14px',
            color: 'var(--frs-text-muted, var(--chakra-colors-gray-600))',
            fontWeight: '500',
            gap: '1px',
            flexWrap: 'nowrap',
            flex: '0 1 auto',
          }),
          singleValue: (provided) => ({
            ...provided,
            color: 'var(--frs-text-muted, var(--chakra-colors-gray-600))',
            fontWeight: '500',
            fontSize: '14px',
            margin: 0,
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            padding: 0,
            height: '20px',
            width: '14px',
            marginLeft: 'auto',
            justifyContent: 'center',
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            padding: 0,
            color: 'var(--frs-text-muted, var(--chakra-colors-gray-600))',
            marginLeft: 0,
            svg: {
              width: '10px',
              height: '10px',
            },
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          menu: (provided) => ({
            ...provided,
            minWidth: '140px',
            boxShadow: 'md',
            marginTop: '0px',
            paddingY: 0,
            backgroundColor: 'var(--frs-menu-bg, #ffffff)',
          }),
          menuList: (provided) => ({
            ...provided,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          option: (provided, state) => {
            const backgroundColor = state.isFocused
              ? 'var(--frs-option-hover-bg, var(--chakra-colors-gray-50))'
              : 'transparent';
            const textColor = state.isDisabled
              ? 'var(--frs-text-disabled, var(--chakra-colors-gray-400))'
              : 'var(--frs-option-text, var(--chakra-colors-navyBlue-500))';
            return {
              ...provided,
              fontSize: '12px',
              color: textColor,
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              columnGap: '20px',
            };
          },
        }}
      />
    </Box>
  );
};

interface KeywordSelectProps {
  keywords: readonly KeywordOption[];
  onKeywordsChange: (keywords: readonly KeywordOption[]) => void;
  placeholder: string;
  isNot?: boolean;
  onIsNotChange?: (isNot: boolean) => void;
  showNotToggle?: boolean;
}

const KeywordSelect = ({
  keywords,
  onKeywordsChange,
  placeholder,
  isNot = false,
  onIsNotChange,
  showNotToggle = false,
}: KeywordSelectProps) => {
  const [inputState, setInputState] = useState('');
  const innerRef =
    useRef<SelectInstance<KeywordOption, true, GroupBase<KeywordOption>>>(null);

  useMount(() => {
    if (innerRef.current) {
      innerRef?.current?.focus();
    }
  });

  const handleChange = (selectedOptions: MultiValue<KeywordOption>) => {
    const validOptions = selectedOptions.filter(
      (option) => option.value.trim().length > 0
    );
    onKeywordsChange(validOptions);
  };

  const handleInputChange = (inputValue: string) => {
    setInputState(inputValue);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text');
    const pastedValues = paste.split(',').map((val) => val.trim());
    const newOptions = pastedValues
      .filter((value) => value.length > 0)
      .map((value) => ({
        value,
        label: value,
      }));
    onKeywordsChange([...keywords, ...newOptions]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ',') {
      event.preventDefault();
      if (inputState.trim()) {
        const newOption = {
          label: inputState.trim(),
          value: inputState.trim(),
        };
        onKeywordsChange([...keywords, newOption]);
        setInputState('');
      }
    }
  };

  const isValidNewOption = (inputValue: string) => {
    return inputValue.trim().length > 0;
  };

  return (
    <Flex alignItems="baseline" gap={4} w="460px">
      <Flex alignItems="center" gap={2} mr={8}>
        <Text flex="none" fontSize="sm">
          Contains{' '}
          {showNotToggle && onIsNotChange ? (
            <InlineAnyOfNoneOfDropdown isNot={isNot} onChange={onIsNotChange} />
          ) : (
            <Text as="span" className={styles.muted600}>
              any of
            </Text>
          )}
        </Text>
        <Tooltip
          hasArrow
          label="Type or paste a list of comma separated keywords"
          variant="label"
          placement="top"
          maxWidth="170px"
          fontSize="xs"
          lineHeight="15px"
          openDelay={400}
          p={2}
        >
          <InfoOutlineIcon boxSize={3} />
        </Tooltip>
      </Flex>
      <Box w="full" minH="32px" overflow="visible">
        <Box data-testid="keyword-input-box" onPaste={handlePaste}>
          <AsyncCreatableSelect
            ref={innerRef}
            size="sm"
            isMulti
            isClearable={false}
            name="tabs"
            value={keywords || []}
            onChange={handleChange}
            onInputChange={handleInputChange}
            inputValue={inputState}
            inputId="keyword-input-id"
            formatCreateLabel={(input: string) => `Add "${input}"`}
            onKeyDown={handleKeyDown}
            options={[]}
            placeholder={placeholder}
            tagVariant="keywordChip"
            noOptionsMessage={() => null}
            isValidNewOption={isValidNewOption}
            styles={{
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 10,
              }),
            }}
            menuPortalTarget={document.body}
            chakraStyles={{
              control: (provided) => ({
                ...provided,
                height: 'auto',
                minHeight: '32px',
              }),
              // Theme the keyword chips via the --frs-chip-* tokens. In legacy
              // these vars are undefined, so the fallbacks reproduce the
              // `keywordChip` Tag variant byte-for-byte; light/dark resolve to
              // the neutral chip surface/text tokens.
              multiValue: (provided) => ({
                ...provided,
                backgroundColor: 'var(--frs-chip-bg, #ECF9FE)',
                borderColor: 'var(--frs-chip-border, #A2DFF9)',
              }),
              multiValueLabel: (provided) => ({
                ...provided,
                color:
                  'var(--frs-chip-text, var(--chakra-colors-text-primary))',
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color:
                  'var(--frs-chip-text, var(--chakra-colors-text-primary))',
              }),
              valueContainer: (provided) => ({
                ...provided,
                minHeight: '32px',
                maxHeight: '150px',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '12px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 transparent',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#cbd5e0',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#a0aec0',
                },
              }),
            }}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
          />
        </Box>
      </Box>
    </Flex>
  );
};

export const FreeTextFilterForm = ({
  filter,
  closeMenu,
  onSave,
  onBack,
  isNewInstance,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
}: FreeTextFilterProps) => {
  const [keywords, setKeywords] = useState<readonly KeywordOption[]>([]);
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  const location = useLocation();
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const [isNotEnabled, setIsNotEnabled] = useState<boolean>(
    isNotEnabledProp ?? filter.isNot ?? false
  );

  const handleIsNotChange = (isNot: boolean) => {
    setIsNotEnabled(isNot);
    if (onIsNotEnabledChange) {
      onIsNotEnabledChange(isNot);
    }
  };

  useEffect(() => {
    const existingKeywords = filter.text;
    setKeywords(
      existingKeywords.map((keyword: string) => ({
        label: keyword,
        value: keyword,
      }))
    );
    setIsCurrent(filter.isCurrent ?? true);
    setIsNotEnabled(isNotEnabledProp ?? filter.isNot ?? false);
  }, [filter, isNotEnabledProp]);

  const handleClearSelections = () => {
    setKeywords([]);
  };

  const isAddDisabled = keywords.length === 0;

  const handleAddFilter = () => {
    // Filter out whitespace-only items
    const keywordValues = keywords
      .map((keyword) => keyword.value.trim())
      .filter((value) => value.length > 0);

    if (keywordValues.length === 0) {
      return;
    }

    const nextFilter: FreeTextFilter = {
      ...filter,
      text: keywordValues,
      isCurrent,
      isNot: isNotEnabled,
    };
    onSave(nextFilter);
    closeMenu();
  };

  const placeholder =
    filter.id === FilterId.KEYWORDS
      ? 'e.g. linux, hardware, firewall, LAN...'
      : 'e.g. software engineer, data scientist...';

  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  const addLabel = isNewInstance || filter.text.length === 0 ? 'Add' : 'Update';

  return (
    <VStack spacing={4} align="stretch">
      <PopoverBody padding={0}>
        <Box onClick={(e) => e.stopPropagation()}>
          <KeywordSelect
            keywords={keywords}
            onKeywordsChange={setKeywords}
            placeholder={placeholder}
            isNot={isNotEnabled}
            onIsNotChange={handleIsNotChange}
            showNotToggle={showNotToggle}
          />
          {showToggle && (
            <Box padding="8px">
              <ToggleSelect
                filterName={filter.name}
                onChange={(e: number) => setIsCurrent(e === 0)}
                initialIsCurrent={isCurrent}
              />
            </Box>
          )}
        </Box>
      </PopoverBody>
      <PopoverFooter padding={0} borderTopWidth={0}>
        <FilterSelectFooter
          onClearSelections={handleClearSelections}
          onClose={closeMenu}
          onAdd={handleAddFilter}
          addLabel={addLabel}
          isAddDisabled={isAddDisabled}
          onBack={onBack}
        />
      </PopoverFooter>
    </VStack>
  );
};
